'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLoadedSvgSource } from '@/components/tools/svg-tool-shared';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  cleanInlineSvgMarkup,
  cleanSvgViewBox,
  copySvgText,
  createSvgDataUrl,
  createTextDownloadBlob,
  formatFileSize,
  getBaseName,
  normalizeSvgIconMarkup,
  optimizeSvgMarkup,
  renderSvgToPdf,
  renderSvgToPng,
  type SvgBackgroundMode,
  type SvgMetadata,
} from '@/lib/browser-svg-tools';

type Variant =
  | 'svg-optimizer'
  | 'svg-to-png'
  | 'svg-to-jpg'
  | 'svg-to-webp'
  | 'svg-to-pdf'
  | 'svg-dimensions-checker'
  | 'svg-viewbox-cleaner'
  | 'svg-to-data-url'
  | 'inline-svg-cleaner'
  | 'svg-icon-sizing-tool';

type SizeMode = 'width' | 'scale';

const toolCopy: Record<
  Variant,
  {
    title: string;
    description: string;
    badge: string;
    actionLabel: string;
    successLabel: string;
    acceptPaste: boolean;
    inspectOnly?: boolean;
  }
> = {
  'svg-optimizer': {
    title: 'Optimize SVG markup and remove editor junk',
    description:
      'Load one SVG from a file or pasted markup, strip metadata and comments, compact the markup, and export a lighter SVG locally.',
    badge: 'Optimize',
    actionLabel: 'Optimize SVG',
    successLabel: 'SVG optimization',
    acceptPaste: true,
  },
  'svg-to-png': {
    title: 'Export one SVG as PNG at a chosen width or scale',
    description:
      'Rasterize one SVG locally, choose the output size and background, then download a PNG for docs, listings, or social assets.',
    badge: 'PNG',
    actionLabel: 'Export PNG',
    successLabel: 'SVG to PNG export',
    acceptPaste: true,
  },
  'svg-to-jpg': {
    title: 'Export one SVG as JPG at a chosen width or scale',
    description:
      'Rasterize one SVG locally into a JPG at the size you need. Transparent areas flatten onto a white background, which JPG needs, so it is ready for uploads and documents.',
    badge: 'JPG',
    actionLabel: 'Export JPG',
    successLabel: 'SVG to JPG export',
    acceptPaste: true,
  },
  'svg-to-webp': {
    title: 'Export one SVG as WebP at a chosen width or scale',
    description:
      'Rasterize one SVG locally into a WebP at the size you need. WebP keeps transparency and stays small, so it is ready for the web without a server upload.',
    badge: 'WebP',
    actionLabel: 'Export WebP',
    successLabel: 'SVG to WebP export',
    acceptPaste: true,
  },
  'svg-to-pdf': {
    title: 'Export one SVG as a PDF at a chosen width or scale',
    description:
      'Rasterize one SVG locally and wrap it in a single-page PDF sized to the image, ready to share, print, or attach, without a server upload.',
    badge: 'PDF',
    actionLabel: 'Export PDF',
    successLabel: 'SVG to PDF export',
    acceptPaste: true,
  },
  'svg-dimensions-checker': {
    title: 'Inspect width, height, viewBox, and aspect ratio',
    description:
      'Check the intrinsic dimensions of one SVG, flag missing width or viewBox values, and copy the important geometry fields for frontend handoff.',
    badge: 'Inspect',
    actionLabel: 'Analyze SVG',
    successLabel: 'SVG dimension analysis',
    acceptPaste: true,
    inspectOnly: true,
  },
  'svg-viewbox-cleaner': {
    title: 'Fix the viewBox and trim empty SVG space',
    description:
      'Detect missing or weak viewBox values, normalize the bounds, trim empty canvas space, and export a cleaner SVG locally.',
    badge: 'viewBox',
    actionLabel: 'Clean viewBox',
    successLabel: 'SVG viewBox cleanup',
    acceptPaste: true,
  },
  'svg-to-data-url': {
    title: 'Convert one SVG into a data URL and CSS snippet',
    description:
      'Minify the SVG first, then generate a compact data URL and a ready-to-paste CSS background-image line for frontend use.',
    badge: 'Data URL',
    actionLabel: 'Generate output',
    successLabel: 'SVG data URL generation',
    acceptPaste: true,
  },
  'inline-svg-cleaner': {
    title: 'Clean SVG markup for inline embeds and components',
    description:
      'Paste editor-exported SVG, strip junk, sanitize the markup, and export a cleaner inline-ready SVG snippet for frontend handoff.',
    badge: 'Inline',
    actionLabel: 'Clean markup',
    successLabel: 'Inline SVG cleanup',
    acceptPaste: true,
  },
  'svg-icon-sizing-tool': {
    title: 'Normalize an SVG icon to a clean design-system grid',
    description:
      'Trim empty space, normalize the artboard, and export one icon-sized SVG on a 16, 20, 24, or 32 grid for product UI work.',
    badge: 'Icons',
    actionLabel: 'Normalize icon',
    successLabel: 'SVG icon normalization',
    acceptPaste: true,
  },
};

function createObjectUrl(blob: Blob) {
  return URL.createObjectURL(blob);
}

export default function BrowserSvgMarkupTool({ variant }: { variant: Variant }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { source, loadFile, loadMarkup, clearSource } = useLoadedSvgSource();
  const [pastedMarkup, setPastedMarkup] = useState('');
  const [sizeMode, setSizeMode] = useState<SizeMode>('width');
  const [outputWidth, setOutputWidth] = useState(1024);
  const [scale, setScale] = useState(2);
  const [backgroundMode, setBackgroundMode] = useState<SvgBackgroundMode>('transparent');
  const [customBackground, setCustomBackground] = useState('#ffffff');
  const [gridSize, setGridSize] = useState<16 | 20 | 24 | 32>(24);
  const [centerMode, setCenterMode] = useState<'center' | 'top-left'>('center');
  const [trimEmptySpace, setTrimEmptySpace] = useState(true);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultName, setResultName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [resultMarkup, setResultMarkup] = useState<string | null>(null);
  const [dataUrlText, setDataUrlText] = useState<string | null>(null);
  const [cssSnippetText, setCssSnippetText] = useState<string | null>(null);
  const [analyzedMetadata, setAnalyzedMetadata] = useState<SvgMetadata | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);

  const copy = toolCopy[variant];
  const supportsDownload =
    variant !== 'svg-dimensions-checker' || Boolean(resultUrl) || Boolean(resultMarkup);

  useEffect(() => {
    return () => {
      if (resultUrl) {
        URL.revokeObjectURL(resultUrl);
      }
    };
  }, [resultUrl]);

  const resultPreviewUrl = useMemo(() => {
    if (!resultUrl) {
      return null;
    }

    return variant === 'svg-dimensions-checker' ||
      variant === 'svg-to-data-url' ||
      variant === 'inline-svg-cleaner' ||
      variant === 'svg-to-pdf'
      ? null
      : resultUrl;
  }, [resultUrl, variant]);

  function resetOutputState() {
    if (resultUrl) {
      URL.revokeObjectURL(resultUrl);
    }

    setResultUrl(null);
    setResultName(null);
    setResultSize(null);
    setResultMarkup(null);
    setDataUrlText(null);
    setCssSnippetText(null);
    setAnalyzedMetadata(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    try {
      await loadFile(file);
    } catch (error) {
      clearSource();
      setErrorMessage(error instanceof Error ? error.message : 'The SVG could not be loaded.');
    }
  }

  function handleMarkupLoad() {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      loadMarkup(pastedMarkup);
    } catch (error) {
      clearSource();
      setErrorMessage(
        error instanceof Error ? error.message : 'The pasted SVG could not be loaded.',
      );
    }
  }

  async function copyTextOutput(kind: 'data-url' | 'css' | 'markup' | 'dimensions') {
    try {
      const text =
        kind === 'data-url'
          ? dataUrlText
          : kind === 'css'
            ? cssSnippetText
            : kind === 'markup'
              ? resultMarkup
              : analyzedMetadata
                ? [
                    `width: ${analyzedMetadata.width ?? 'missing'}`,
                    `height: ${analyzedMetadata.height ?? 'missing'}`,
                    `viewBox: ${analyzedMetadata.viewBox ?? 'missing'}`,
                    `aspect-ratio: ${analyzedMetadata.aspectRatio}`,
                  ].join('\n')
                : null;

      if (!text) {
        return;
      }

      await copySvgText(text);
      setCreditMessage(
        kind === 'data-url'
          ? 'Data URL copied.'
          : kind === 'css'
            ? 'CSS snippet copied.'
            : kind === 'markup'
              ? 'SVG markup copied.'
              : 'SVG dimensions copied.',
      );
    } catch {
      setErrorMessage('The browser could not copy the current SVG output.');
    }
  }

  async function runTool() {
    if (!source) {
      setErrorMessage('Choose one SVG or paste valid SVG markup first.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        variant,
        'Credits are not available for another SVG tool run right now.',
      );
      const baseName = getBaseName(source.name || 'graphic.svg');

      resetOutputState();

      if (variant === 'svg-optimizer') {
        const optimizedMarkup = optimizeSvgMarkup(source.originalMarkup);
        const blob = createTextDownloadBlob(optimizedMarkup, 'image/svg+xml;charset=utf-8');
        setResultMarkup(optimizedMarkup);
        setResultSize(blob.size);
        setResultUrl(createObjectUrl(blob));
        setResultName(`${baseName}-optimized.svg`);
      } else if (
        variant === 'svg-to-png' ||
        variant === 'svg-to-jpg' ||
        variant === 'svg-to-webp'
      ) {
        const format =
          variant === 'svg-to-jpg' ? 'jpeg' : variant === 'svg-to-webp' ? 'webp' : 'png';
        const extension =
          variant === 'svg-to-jpg' ? 'jpg' : variant === 'svg-to-webp' ? 'webp' : 'png';
        const blob = await renderSvgToPng(source.originalMarkup, {
          outputWidth: sizeMode === 'width' ? outputWidth : null,
          scale: sizeMode === 'scale' ? scale : null,
          backgroundMode,
          customBackground,
          format,
        });
        setResultSize(blob.size);
        setResultUrl(createObjectUrl(blob));
        setResultName(`${baseName}.${extension}`);
      } else if (variant === 'svg-to-pdf') {
        const blob = await renderSvgToPdf(source.originalMarkup, {
          outputWidth: sizeMode === 'width' ? outputWidth : null,
          scale: sizeMode === 'scale' ? scale : null,
          backgroundMode,
          customBackground,
        });
        setResultSize(blob.size);
        setResultUrl(createObjectUrl(blob));
        setResultName(`${baseName}.pdf`);
      } else if (variant === 'svg-dimensions-checker') {
        setAnalyzedMetadata(source.metadata);
      } else if (variant === 'svg-viewbox-cleaner') {
        const cleanedMarkup = cleanSvgViewBox(source.originalMarkup);
        const blob = createTextDownloadBlob(cleanedMarkup, 'image/svg+xml;charset=utf-8');
        setResultMarkup(cleanedMarkup);
        setResultSize(blob.size);
        setResultUrl(createObjectUrl(blob));
        setResultName(`${baseName}-cleaned.svg`);
        setAnalyzedMetadata(source.metadata);
      } else if (variant === 'svg-to-data-url') {
        const output = createSvgDataUrl(source.originalMarkup);
        const blob = createTextDownloadBlob(
          `${output.dataUrl}\n\n${output.cssSnippet}`,
          'text/plain;charset=utf-8',
        );
        setResultMarkup(output.optimizedMarkup);
        setDataUrlText(output.dataUrl);
        setCssSnippetText(output.cssSnippet);
        setResultSize(blob.size);
        setResultUrl(createObjectUrl(blob));
        setResultName(`${baseName}-data-url.txt`);
      } else if (variant === 'inline-svg-cleaner') {
        const cleanedMarkup = cleanInlineSvgMarkup(source.originalMarkup);
        const blob = createTextDownloadBlob(cleanedMarkup, 'image/svg+xml;charset=utf-8');
        setResultMarkup(cleanedMarkup);
        setResultSize(blob.size);
        setResultUrl(createObjectUrl(blob));
        setResultName(`${baseName}-inline.svg`);
      } else if (variant === 'svg-icon-sizing-tool') {
        const normalizedMarkup = normalizeSvgIconMarkup(source.originalMarkup, {
          gridSize,
          centerMode,
          trimEmptySpace,
        });
        const blob = createTextDownloadBlob(normalizedMarkup, 'image/svg+xml;charset=utf-8');
        setResultMarkup(normalizedMarkup);
        setResultSize(blob.size);
        setResultUrl(createObjectUrl(blob));
        setResultName(`${baseName}-${gridSize}.svg`);
      }

      setCreditMessage(formatToolCreditSuccess(copy.successLabel, credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The SVG action could not be completed.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  const metadata = analyzedMetadata ?? (copy.inspectOnly ? null : null);

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{copy.title}</h2>
          <p>{copy.description}</p>
        </div>
        <span className="tool-live-badge">{copy.badge}</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.name : 'Choose one SVG or drop it here'}</strong>
              <span>
                {source
                  ? `${formatFileSize(source.originalSize)} • ${source.metadata.width ?? 'auto'} × ${source.metadata.height ?? 'auto'}`
                  : 'Upload one .svg file or paste raw SVG markup below'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/svg+xml,.svg"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          {copy.acceptPaste ? (
            <>
              <label className="tool-live-field tool-live-field-full">
                <span>SVG markup</span>
                <textarea
                  onChange={(event) => setPastedMarkup(event.target.value)}
                  placeholder="<svg viewBox='0 0 24 24'>…</svg>"
                  rows={7}
                  value={pastedMarkup}
                />
              </label>
              <div className="hero-actions tool-live-actions">
                <button
                  className="button button-ghost"
                  disabled={!pastedMarkup.trim()}
                  onClick={handleMarkupLoad}
                  type="button"
                >
                  Use pasted SVG
                </button>
              </div>
            </>
          ) : null}

          {variant === 'svg-to-png' ||
          variant === 'svg-to-jpg' ||
          variant === 'svg-to-webp' ||
          variant === 'svg-to-pdf' ? (
            <>
              <div className="tool-live-routes" aria-label="PNG sizing mode">
                <button
                  className={`tool-live-route ${sizeMode === 'width' ? 'tool-live-route-active' : ''}`}
                  onClick={() => setSizeMode('width')}
                  type="button"
                >
                  Set width
                </button>
                <button
                  className={`tool-live-route ${sizeMode === 'scale' ? 'tool-live-route-active' : ''}`}
                  onClick={() => setSizeMode('scale')}
                  type="button"
                >
                  Use scale
                </button>
              </div>

              <div className="tool-live-controls">
                {sizeMode === 'width' ? (
                  <label className="tool-live-field">
                    <span>Output width</span>
                    <input
                      min="16"
                      onChange={(event) => {
                        setOutputWidth(Math.max(16, Number(event.target.value) || 16));
                        resetOutputState();
                      }}
                      type="number"
                      value={outputWidth}
                    />
                  </label>
                ) : (
                  <label className="tool-live-field">
                    <span>Scale</span>
                    <input
                      max="8"
                      min="0.25"
                      onChange={(event) => {
                        setScale(Math.max(0.25, Number(event.target.value) || 1));
                        resetOutputState();
                      }}
                      step="0.25"
                      type="number"
                      value={scale}
                    />
                  </label>
                )}

                <label className="tool-live-field">
                  <span>Background</span>
                  <select
                    onChange={(event) => {
                      setBackgroundMode(event.target.value as SvgBackgroundMode);
                      resetOutputState();
                    }}
                    value={backgroundMode}
                  >
                    <option value="transparent">Transparent</option>
                    <option value="white">White</option>
                    <option value="black">Black</option>
                    <option value="custom">Custom</option>
                  </select>
                </label>

                {backgroundMode === 'custom' ? (
                  <label className="tool-live-field">
                    <span>Custom color</span>
                    <input
                      onChange={(event) => {
                        setCustomBackground(event.target.value);
                        resetOutputState();
                      }}
                      type="color"
                      value={customBackground}
                    />
                  </label>
                ) : null}
              </div>
            </>
          ) : null}

          {variant === 'svg-icon-sizing-tool' ? (
            <>
              <div className="tool-live-routes" aria-label="Icon grid size">
                {[16, 20, 24, 32].map((value) => (
                  <button
                    key={value}
                    className={`tool-live-route ${gridSize === value ? 'tool-live-route-active' : ''}`}
                    onClick={() => {
                      setGridSize(value as 16 | 20 | 24 | 32);
                      resetOutputState();
                    }}
                    type="button"
                  >
                    {value}
                  </button>
                ))}
              </div>

              <div className="tool-live-controls">
                <label className="tool-live-field">
                  <span>Alignment</span>
                  <select
                    onChange={(event) => {
                      setCenterMode(event.target.value as 'center' | 'top-left');
                      resetOutputState();
                    }}
                    value={centerMode}
                  >
                    <option value="center">Center content</option>
                    <option value="top-left">Top-left</option>
                  </select>
                </label>
                <label className="tool-live-field">
                  <span>Trim empty space</span>
                  <select
                    onChange={(event) => {
                      setTrimEmptySpace(event.target.value === 'yes');
                      resetOutputState();
                    }}
                    value={trimEmptySpace ? 'yes' : 'no'}
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </label>
              </div>
            </>
          ) : null}

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing}
              onClick={() => void runTool()}
              type="button"
            >
              {isProcessing ? 'Processing…' : copy.actionLabel}
            </button>
            {supportsDownload && resultUrl && resultName ? (
              <a className="button button-ghost" download={resultName} href={resultUrl}>
                Download result
              </a>
            ) : null}
            {variant === 'svg-to-data-url' && dataUrlText ? (
              <button
                className="button button-ghost"
                onClick={() => void copyTextOutput('data-url')}
                type="button"
              >
                Copy data URL
              </button>
            ) : null}
            {variant === 'svg-to-data-url' && cssSnippetText ? (
              <button
                className="button button-ghost"
                onClick={() => void copyTextOutput('css')}
                type="button"
              >
                Copy CSS
              </button>
            ) : null}
            {variant === 'inline-svg-cleaner' && resultMarkup ? (
              <button
                className="button button-ghost"
                onClick={() => void copyTextOutput('markup')}
                type="button"
              >
                Copy SVG
              </button>
            ) : null}
            {variant === 'svg-dimensions-checker' && analyzedMetadata ? (
              <button
                className="button button-ghost"
                onClick={() => void copyTextOutput('dimensions')}
                type="button"
              >
                Copy values
              </button>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>
            {variant === 'svg-dimensions-checker'
              ? 'SVG geometry'
              : variant === 'svg-to-data-url'
                ? 'Output summary'
                : 'Export summary'}
          </h3>
          <div className="tool-live-stats">
            <div>
              <strong>Source</strong>
              <span>{source ? formatFileSize(source.originalSize) : 'No SVG loaded'}</span>
            </div>
            <div>
              <strong>Sanitized size</strong>
              <span>{source ? formatFileSize(source.sanitizedSize) : 'Not loaded yet'}</span>
            </div>
            <div>
              <strong>Result</strong>
              <span>
                {resultSize
                  ? formatFileSize(resultSize)
                  : variant === 'svg-dimensions-checker'
                    ? 'Analysis only'
                    : 'Not exported yet'}
              </span>
            </div>
            <div>
              <strong>Privacy</strong>
              <span>Processed in your browser</span>
            </div>
          </div>

          {source?.metadata.warnings.length ? (
            <div className="tool-live-panel-note">
              <strong>Warnings</strong>
              <span>{source.metadata.warnings.join(' ')}</span>
            </div>
          ) : null}
        </aside>
      </div>

      {variant === 'svg-dimensions-checker' && analyzedMetadata ? (
        <div className="tool-live-preview">
          <h3>Dimensions</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Width</strong>
              <span>{analyzedMetadata.width ?? 'Missing'}</span>
            </div>
            <div>
              <strong>Height</strong>
              <span>{analyzedMetadata.height ?? 'Missing'}</span>
            </div>
            <div>
              <strong>viewBox</strong>
              <span>{analyzedMetadata.viewBox ?? 'Missing'}</span>
            </div>
            <div>
              <strong>Aspect ratio</strong>
              <span>{analyzedMetadata.aspectRatio}</span>
            </div>
          </div>
        </div>
      ) : null}

      {variant === 'svg-to-data-url' && (dataUrlText || cssSnippetText) ? (
        <div className="tool-live-preview">
          <h3>Generated output</h3>
          {dataUrlText ? (
            <label className="tool-live-field tool-live-field-full">
              <span>Data URL</span>
              <textarea readOnly rows={5} value={dataUrlText} />
            </label>
          ) : null}
          {cssSnippetText ? (
            <label className="tool-live-field tool-live-field-full">
              <span>CSS background-image</span>
              <textarea readOnly rows={4} value={cssSnippetText} />
            </label>
          ) : null}
        </div>
      ) : null}

      {(resultPreviewUrl || source) &&
      variant !== 'svg-dimensions-checker' &&
      variant !== 'svg-to-data-url' &&
      variant !== 'inline-svg-cleaner' ? (
        <div className="tool-live-preview">
          <h3>Preview</h3>
          <div className="tool-image-compare-grid">
            <div className="tool-image-compare-card">
              <strong>Source SVG</strong>
              <div className="tool-live-preview-frame">
                {source ? (
                  <img alt="Source SVG preview" src={source.previewUrl} />
                ) : (
                  <div className="tool-editor-empty">
                    <strong>Source preview</strong>
                    <span>Load one SVG to preview the source asset.</span>
                  </div>
                )}
              </div>
            </div>
            <div className="tool-image-compare-card">
              <strong>
                {variant === 'svg-to-png'
                  ? 'PNG result'
                  : variant === 'svg-to-jpg'
                    ? 'JPG result'
                    : variant === 'svg-to-webp'
                      ? 'WebP result'
                      : variant === 'svg-to-pdf'
                        ? 'PDF result'
                        : variant === 'svg-viewbox-cleaner'
                          ? 'Cleaned SVG'
                          : variant === 'svg-icon-sizing-tool'
                            ? 'Normalized icon'
                            : 'Optimized SVG'}
              </strong>
              <div className="tool-live-preview-frame">
                {resultPreviewUrl ? (
                  <img alt="SVG tool result preview" src={resultPreviewUrl} />
                ) : (
                  <div className="tool-editor-empty">
                    <strong>Result preview</strong>
                    <span>Run the tool to preview the export.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {variant === 'inline-svg-cleaner' && resultMarkup ? (
        <div className="tool-live-preview">
          <h3>Cleaned markup</h3>
          <label className="tool-live-field tool-live-field-full">
            <span>Inline-ready SVG</span>
            <textarea readOnly rows={10} value={resultMarkup} />
          </label>
        </div>
      ) : null}
    </article>
  );
}
